import { useSyncExternalStore } from 'react';

/**
 * Minimal hash routing: `#/` is the Foe Elevator, `#/floor/<id>` a floor's mission. Hash routes
 * work from any static host and inside native web views without server configuration; query
 * parameters (development switches such as `?mic=sim`) stay untouched.
 */
/** `run` distinguishes replays of the same floor, so a replay starts a fresh mission. */
export type Route =
  | { page: 'elevator' }
  | { page: 'intro' }
  /** The agent builder: the child's look and codename. */
  | { page: 'agent' }
  /** Alex's Lab: the 3D mouth in the story (docs/design/alex-lab.md). */
  | { page: 'lab' }
  /** Development and QA: the debug tools (features/debug), on one of their tabs. */
  | { page: 'debug'; tab?: string }
  | { page: 'floor'; floorId: string; run?: string };

export function parseRoute(hash: string): Route {
  if (hash.startsWith('#/intro')) return { page: 'intro' };
  if (hash.startsWith('#/agent')) return { page: 'agent' };
  if (hash.startsWith('#/lab')) return { page: 'lab' };
  if (hash.startsWith('#/debug')) {
    const tab = /^#\/debug\/([\w-]+)/.exec(hash)?.[1];
    return tab ? { page: 'debug', tab } : { page: 'debug' };
  }
  const match = /^#\/floor\/([\w-]+)(?:\/(\w+))?/.exec(hash);
  if (!match?.[1]) return { page: 'elevator' };
  return match[2]
    ? { page: 'floor', floorId: match[1], run: match[2] }
    : { page: 'floor', floorId: match[1] };
}

export function routeHash(route: Route): string {
  if (route.page === 'elevator') return '#/';
  if (route.page === 'intro') return '#/intro';
  if (route.page === 'agent') return '#/agent';
  if (route.page === 'lab') return '#/lab';
  if (route.page === 'debug') return route.tab ? `#/debug/${route.tab}` : '#/debug';
  return route.run ? `#/floor/${route.floorId}/${route.run}` : `#/floor/${route.floorId}`;
}

export function navigate(route: Route): void {
  window.location.hash = routeHash(route);
}

function subscribe(listener: () => void) {
  window.addEventListener('hashchange', listener);
  return () => {
    window.removeEventListener('hashchange', listener);
  };
}

const getHash = () => window.location.hash;

export function useRoute(): Route {
  return parseRoute(useSyncExternalStore(subscribe, getHash));
}
